import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import CaseContextHeader from "../components/CaseContextHeader";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Upload, FlaskConical, Trash2, AlertTriangle, FileText,
} from "lucide-react";

const CATEGORIES = [
  "Party Submission", "SDM / Prant Order", "Collector Order",
  "SSRD Sample Order", "Government Resolution / GR",
  "Citation / Judgment", "Evidence / Annexure", "Other",
];
const LANGUAGES = ["Gujarati", "English", "Mixed", "Hindi"];
const FORMATS = ["Digital Typed", "Typed Scanned", "Handwritten", "Unknown"];

export default function Documents() {
  const params = useParams();
  const [caseId, setCaseId] = useState(params.caseId || "");
  const [cases, setCases] = useState([]);
  const [caseDoc, setCaseDoc] = useState(null);
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({
    document_title: "", document_category: "Party Submission",
    document_language: "Gujarati", document_format: "Typed Scanned",
    remarks: "", file: null,
  });
  const [uploading, setUploading] = useState(false);

  // Load case list if no caseId selected
  useEffect(() => {
    if (!caseId) {
      api.get("/cases").then((r) => setCases(r.data));
    } else {
      api.get(`/cases/${caseId}`).then((r) => {
        setCaseDoc(r.data.case);
        setDocs(r.data.documents);
      });
    }
  }, [caseId]);

  const reloadDocs = () => api.get(`/cases/${caseId}/documents`).then((r) => setDocs(r.data));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) return toast.error("Please select a file");
    if (!form.document_title) return toast.error("Title required");
    if (form.document_format === "Handwritten") {
      const ok = window.confirm(
        "Handwritten OCR is outside POC scope. The system will store the file but will not run OCR. Continue?",
      );
      if (!ok) return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("document_title", form.document_title);
    fd.append("document_category", form.document_category);
    fd.append("document_language", form.document_language);
    fd.append("document_format", form.document_format);
    fd.append("remarks", form.remarks);
    fd.append("file", form.file);
    try {
      await api.post(`/cases/${caseId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      setForm({ ...form, document_title: "", remarks: "", file: null });
      document.getElementById("doc-file-input").value = "";
      reloadDocs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const runOcr = async (id) => {
    try {
      await api.post(`/documents/${id}/process`);
      toast.info("Processing started — refresh in a moment");
      setTimeout(reloadDocs, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    reloadDocs();
  };

  if (!caseId) {
    return (
      <Layout breadcrumbs={["SSRD", "Documents"]}>
        <h1 className="text-2xl font-bold mb-4">Pick a case</h1>
        <p className="text-sm text-muted-foreground mb-4">Document upload is scoped to a case.</p>
        <Card><CardContent className="p-0"><Table>
          <TableHeader><TableRow>
            <TableHead>Case</TableHead><TableHead>District</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {cases.map((c) => (
              <TableRow key={c.case_id}>
                <TableCell className="font-mono text-xs">{c.case_number}/{c.case_year}</TableCell>
                <TableCell>{c.district}</TableCell>
                <TableCell><StatusBadge status={c.case_status} /></TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => setCaseId(c.case_id)} data-testid={`pick-case-${c.case_id}`}>Open</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></CardContent></Card>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={[
      { label: "SSRD", to: "/dashboard" },
      { label: "Cases", to: "/cases" },
      caseDoc ? { label: `${caseDoc.case_number}/${caseDoc.case_year}`, to: `/cases/${caseId}` } : "…",
      "Documents",
    ]}>
      <CaseContextHeader caseDoc={caseDoc} />

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
        <Card data-testid="doc-upload-card">
          <CardContent className="p-5">
            <div className="text-[10px] tracking-widest text-muted-foreground uppercase mb-1">Upload</div>
            <h2 className="text-xl font-semibold mb-4">New Document</h2>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs">Document Title *</Label>
                <Input data-testid="doc-title-input" value={form.document_title} onChange={(e) => setForm({ ...form, document_title: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.document_category} onValueChange={(v) => setForm({ ...form, document_category: v })}>
                  <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Language</Label>
                  <Select value={form.document_language} onValueChange={(v) => setForm({ ...form, document_language: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Format</Label>
                  <Select value={form.document_format} onValueChange={(v) => setForm({ ...form, document_format: v })}>
                    <SelectTrigger data-testid="doc-format-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {form.document_format === "Handwritten" && (
                <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs p-3 flex gap-2" data-testid="handwritten-warning">
                  <AlertTriangle className="size-4 shrink-0 mt-0.5" />
                  <span>Handwritten OCR is outside POC scope. Manual review required.</span>
                </div>
              )}
              <div>
                <Label className="text-xs">File (PDF / Image)</Label>
                <Input id="doc-file-input" type="file" accept=".pdf,image/*"
                  onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                  data-testid="doc-file-input" />
              </div>
              <div>
                <Label className="text-xs">Remarks</Label>
                <Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
              </div>
              <Button disabled={uploading} type="submit" className="w-full gap-2" data-testid="doc-upload-submit">
                <Upload className="size-4" /> {uploading ? "Uploading…" : "Upload"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card data-testid="doc-list-card"><CardContent className="p-0">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <div className="text-lg font-semibold">Uploaded Documents</div>
            <Link to={`/cases/${caseId}/processing`} className="text-xs text-primary hover:underline">Go to processing →</Link>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>File</TableHead><TableHead>Category</TableHead><TableHead>Fmt</TableHead>
              <TableHead>OCR</TableHead><TableHead>Pages</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {docs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8 text-sm">No documents yet.</TableCell></TableRow>}
              {docs.map((d) => (
                <TableRow key={d.document_id} data-testid={`doc-row-${d.document_id}`}>
                  <TableCell className="text-xs flex items-center gap-2"><FileText className="size-3.5 text-muted-foreground" /> {d.file_name}</TableCell>
                  <TableCell className="text-xs">{d.document_category}</TableCell>
                  <TableCell className="text-xs">{d.document_format}</TableCell>
                  <TableCell className="text-xs">{d.ocr_required ? "Yes" : "No"}</TableCell>
                  <TableCell className="text-xs">{d.page_count}</TableCell>
                  <TableCell><StatusBadge status={d.processing_status || d.upload_status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      {!d.handwritten_detected && (
                        <Button size="sm" variant="outline" onClick={() => runOcr(d.document_id)} className="gap-1" data-testid={`process-btn-${d.document_id}`}>
                          <FlaskConical className="size-3.5" /> Process
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => del(d.document_id)} data-testid={`delete-btn-${d.document_id}`}>
                        <Trash2 className="size-4 text-red-700" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      </div>
    </Layout>
  );
}
